/**
 * Encrypts the provided data using AES-GCM with a generated IV and a crypto key retrieved from storage.
 *
 * @async
 * @function encryptData
 * @param {Object} data - The data to be encrypted.
 * @param {any} storage - The storage mechanism used to retrieve the crypto key.
 * @returns {Promise<{cipher: string, iv: number[]}>} An object containing the base64-encoded cipher text and
 * the initialization vector (IV) as an array of numbers.
 */
export const encryptData = async (data, key) =>{
  const initializationVectorLength = 12;
  const iv = crypto.getRandomValues(new Uint8Array(initializationVectorLength));
  const encoded = new TextEncoder().encode(JSON.stringify(data));

  const cipherBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  return {
    cipher: btoa(String.fromCharCode(...new Uint8Array(cipherBuffer))),
    iv: Array.from(iv),
  };
}

/**
 * Decrypts AES-GCM encrypted data using a provided initialization vector and storage for the key.
 *
 * @async
 * @function decryptData
 * @param {string} cipher - The base64-encoded ciphertext to decrypt.
 * @param {Uint8Array|Array|number[]} initVector - The initialization vector (IV) used for decryption.
 * @param {any} storage - The storage object or identifier used to retrieve the crypto key.
 * @returns {Promise<Object>} The decrypted data as a JavaScript object.
 */
export const decryptData = async (cipher, initVector, key) => {
  const cipherBytes = Uint8Array.from(atob(cipher), c => c.charCodeAt(0));

  const plainBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(initVector) },
    key,
    cipherBytes
  );

  return JSON.parse(new TextDecoder().decode(plainBuffer));
}